import { FC, ReactNode } from 'react';
import styles from './QualityCheckInitialScreen.module.css';

export interface SectionData {
  id: string;
  title: string;
  icon?: ReactNode;
  cards: Array<{ id: string; content: ReactNode }>;
}

interface QualityCheckInitialScreenProps {
  sections: SectionData[];
  className?: string;
  layoutType?: 'grid' | 'flex' | 'subgrid';
}

const QualityCheckInitialScreen: FC<QualityCheckInitialScreenProps> = ({
  sections,
  className = '',
  layoutType = 'grid',
}) => {
  // Get container class based on layout type
  const getContainerClass = () => {
    switch (layoutType) {
      case 'flex':
        return styles['flex-intelligent-grid'];
      case 'subgrid':
        return styles['sections-container'];
      case 'grid':
      default:
        return styles['intelligent-grid'];
    }
  };

  // Get section class based on card count and layout type
  const getSectionClass = (cardCount: number) => {
    const isSingleCard = cardCount === 1;

    switch (layoutType) {
      case 'flex':
        return isSingleCard
          ? styles['section--flex-single']
          : styles['section--flex-multi'];
      case 'subgrid':
        return isSingleCard
          ? styles['section--subgrid-single']
          : styles['section--subgrid-multi'];
      case 'grid':
      default:
        return isSingleCard
          ? styles['section--single-card']
          : styles['section--multi-card'];
    }
  };

  // Get cards container class based on card count (fallback for browsers without :has())
  const getCardsContainerClass = (cardCount: number) => {
    if (cardCount === 1) return styles['cards-container--single'];
    if (cardCount === 2) return styles['cards-container--double'];
    return styles['cards-container--multiple'];
  };

  return (
    <div className={`${getContainerClass()} ${className}`}>
      {sections.map(section => (
        <div
          key={section.id}
          className={`${styles.section} ${getSectionClass(
            section.cards.length
          )}`}
        >
          <div className={styles['section-header']}>
            {section.icon && (
              <div className={styles['section-icon']}>{section.icon}</div>
            )}
            <h3 className={styles['section-title']}>{section.title}</h3>
          </div>
          <div
            className={`${styles['cards-container']} ${getCardsContainerClass(
              section.cards.length
            )}`}
          >
            {section.cards.map(card => (
              <div key={card.id}>{card.content}</div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

QualityCheckInitialScreen.displayName = 'QualityCheckInitialScreen';

export default QualityCheckInitialScreen;
